import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import * as microsoftTeams from '@microsoft/teams-js';
import { tokenRequest } from './msalConfig';
import type { AuthContextType, UserInfo } from './types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Detects if the app is running inside Microsoft Teams
 */
function isInTeams(): boolean {
  return window.parent !== window.self && window.name === 'embedded-page-container';
}

/**
 * AuthProvider component that wraps the app and provides authentication context
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || null);
  
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isTeamsContext, setIsTeamsContext] = useState<boolean>(false);

  // Initialize Teams context if running in Teams
  useEffect(() => {
    if (isInTeams()) {
      microsoftTeams.app.initialize().then(() => {
        setIsTeamsContext(true);
        console.log('Teams context initialized');
      }).catch((error) => {
        console.error('Teams initialization error:', error);
      });
    }
  }, []);

  // Update user info when account changes
  useEffect(() => {
    if (account) {
      setUser({
        username: account.username,
        name: account.name || account.username,
        email: account.username,
        roles: account.idTokenClaims?.roles as string[] | undefined,
      });
      setIsLoading(false);
    } else {
      setUser(null);
      // Only set loading to false if no interaction is in progress
      if (inProgress === InteractionStatus.None) {
        setIsLoading(false);
      }
    }
  }, [account, inProgress]);

  /**
   * Login using redirect flow
   */
  const login = async (): Promise<void> => {
    try {
      setError(null);
      await instance.loginRedirect({
        scopes: tokenRequest.scopes,
      });
    } catch (error) {
      console.error('Login error:', error);
      setError(error instanceof Error ? error.message : 'Login failed');
      throw error;
    }
  };

  /**
   * Logout and clear cache
   */
  const logout = async (): Promise<void> => {
    try {
      setError(null);
      await instance.logoutRedirect({
        account: account || undefined,
      });
    } catch (error) {
      console.error('Logout error:', error);
      setError(error instanceof Error ? error.message : 'Logout failed');
      throw error;
    }
  };

  /**
   * Acquire token silently, with fallback to popup if needed
   */
  const acquireTokenSilent = async (): Promise<string | null> => {
    if (!account) {
      console.warn('No account available for token acquisition');
      return null;
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...tokenRequest,
        account: account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.error('Silent token acquisition failed - user session expired. Error:', error.errorCode);
        // Set error message for user notification
        setError('Your session has expired. Please log in again.');
        // Mark that we need to redirect to login
        sessionStorage.setItem('auth:token_expired', 'true');
        // Perform logout which will clear the cache and redirect
        try {
          await instance.logoutRedirect({
            account: account || undefined,
          });
        } catch (logoutError) {
          console.error('Logout failed:', logoutError);
          // If logout fails, at least clear the session storage and navigate to login
          window.location.href = '/login';
        }
        return null;
      }
      console.error('Token acquisition error:', error);
      setError(error instanceof Error ? error.message : 'Token acquisition failed');
      return null;
    }
  };

  /**
   * Acquire token with Teams SSO fallback
   * First tries silent acquisition, then popup, then Teams SSO if in Teams context
   */
  const acquireToken = async (): Promise<string | null> => {
    // Try silent acquisition first
    let token = await acquireTokenSilent();
    if (token) {
      return token;
    }

    // If not in Teams, try popup
    if (!isTeamsContext) {
      try {
        const response = await instance.acquireTokenPopup({
          ...tokenRequest,
          account: account || undefined,
        });
        return response.accessToken;
      } catch (error) {
        console.error('Popup token acquisition failed:', error);
        setError(error instanceof Error ? error.message : 'Token acquisition failed');
        return null;
      }
    }

    // If in Teams, try Teams SSO
    try {
      console.log('Attempting Teams SSO token acquisition');
      const teamsToken = await microsoftTeams.authentication.getAuthToken({
        resources: [import.meta.env.VITE_AZURE_SCOPE],
      });
      return teamsToken;
    } catch (error) {
      console.error('Teams SSO failed:', error);
      setError('Teams SSO authentication failed');
      return null;
    }
  };

  /**
   * Refresh token and update user roles
   * Forces a new token acquisition to get updated roles/claims
   */
  const refreshToken = async (): Promise<void> => {
    if (!account) {
      console.warn('No account available for token refresh');
      return;
    }

    try {
      setError(null);
      setIsLoading(true);
      
      // Force token refresh by clearing cache for this account
      const silentRequest = {
        ...tokenRequest,
        account: account,
        forceRefresh: true, // Force a new token from the server
      };
      
      const response = await instance.acquireTokenSilent(silentRequest);
      
      // Update user info with new token claims
      if (response.account) {
        setUser({
          username: response.account.username,
          name: response.account.name || response.account.username,
          email: response.account.username,
          roles: response.account.idTokenClaims?.roles as string[] | undefined,
        });
      }
      
      console.log('Token refreshed successfully');
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // If interaction is required, try popup
        try {
          const response = await instance.acquireTokenPopup({
            ...tokenRequest,
            account: account,
          });
          
          if (response.account) {
            setUser({
              username: response.account.username,
              name: response.account.name || response.account.username,
              email: response.account.username,
              roles: response.account.idTokenClaims?.roles as string[] | undefined,
            });
          }
          
          console.log('Token refreshed via popup');
        } catch (popupError) {
          console.error('Token refresh popup failed:', popupError);
          setError(popupError instanceof Error ? popupError.message : 'Token refresh failed');
        }
      } else {
        console.error('Token refresh error:', error);
        setError(error instanceof Error ? error.message : 'Token refresh failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const contextValue: AuthContextType = {
    isAuthenticated: !!account,
    isLoading,
    user,
    error,
    login,
    logout,
    acquireToken,
    acquireTokenSilent,
    refreshToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Custom hook to access the auth context
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
